import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Tooltip, Drawer } from '@didi-pebble/pebble-design-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { i18nTranslate } from '@didi/i18n-plugin';

import { getMenuMap, PASSPORT } from '@/config';
import { useIsMobile } from '@/utils';
import {
  setBreadcrumbAtom,
  menuAtom,
  setHeadTitleAtom,
  loginInfoAtom,
  siderOpenAtom,
  setSiderOpen
} from '@/store';
import logo99 from '@/assets/images/logo99.png';
import customerServe from '@/assets/images/customerServe.png';
import { ReactComponent as MenuExpand } from '@/assets/images/menu_expand.svg';
import { ReactComponent as MenuFold } from '@/assets/images/menu_fold.svg';
import { ReactComponent as MenuData } from '@/assets/images/menu_data.svg';
import { ReactComponent as Header } from '@/assets/images/header.svg';
import closeIcon from '@/assets/images/close.png';
import { Omega } from '@/lib';
import goPassport from '@/lib/passport';

import styles from './index.module.less';

import type { MenuProps } from '@didi-pebble/pebble-design-react';

const { Sider } = Layout;

const SiderComp: React.FC = () => {
  const isMobile = useIsMobile();

  type MenuItem = Required<MenuProps>['items'][number];
  const menuList: any = useAtomValue(menuAtom);
  const [collapsed, setCollapsed] = useState(window.innerWidth < 1024);
  const [selectedKeys, setSelectedKeys] = useState(['']);
  const loginInfo: any = useAtomValue(loginInfoAtom);
  const isSiderOpen = useAtomValue(siderOpenAtom);
  const toSetSiderOpen = useSetAtom(setSiderOpen);
  const changeBreadcrumbAtom = useSetAtom(setBreadcrumbAtom);
  const changeHeadTitleAtom = useSetAtom(setHeadTitleAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const menuMap = getMenuMap();

  useEffect(() => {
    const selectKey: string =
      Object.keys(menuMap).find(item => {
        return menuMap[item]?.allRoute.includes(location.pathname);
      }) || '';

    setSelectedKeys([selectKey]);
    changeBreadcrumbAtom({
      selectKey,
      isSubRoute: menuMap[selectKey]?.subRoute === location.pathname
    });
    changeHeadTitleAtom({
      selectKey,
      isSubRoute: menuMap[selectKey]?.subRoute === location.pathname
    });
  }, [location]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1024px)');

    const handleResize = (e: any) => {
      setCollapsed(e.matches);
    };

    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);
  useEffect(() => {
    if (isMobile && isSiderOpen) {
      Omega.trackEvent('ibt_gd_gas_station_business_cs_phonenum_sw', 'h5客服电话展示', {
        user_id: loginInfo?.uid,
        country_code: loginInfo?.country_code
      });
    }
  });
  // 二级目录
  const getSubItems = (subMenuLists: any) => {
    return (
      subMenuLists?.map((item: any) => {
        const label =
          item.submenu_i18n.length > 35 ? (
            <Tooltip title={item.submenu_i18n}>
              <span
                onClick={() =>
                  navigate(menuMap?.[item?.submenu_name]?.subRoute, { replace: false })
                }
              >
                {item.submenu_i18n}
              </span>
            </Tooltip>
          ) : (
            <div
              onClick={() => {
                navigate(menuMap?.[item?.submenu_name]?.subRoute, { replace: false });
                toSetSiderOpen(false);
              }}
            >
              {item.submenu_i18n}
            </div>
          );

        return {
          key: item.submenu_name,
          label
        };
      }) || []
    );
  };

  // 一级目录
  const getItems = (menuLists: any): MenuItem[] => {
    return menuLists
      ?.filter((item: any) => item?.menu_auth?.length > 0)
      ?.map((item: any) => {
        return {
          icon: <MenuData />,
          key: item?.menu_name,
          label: item?.menu_i18n,
          children: getSubItems(item.menu_auth)
        };
      });
  };

  return (
    <>
      {isMobile ? (
        <Drawer
          title={
            <div className='flex items-center justify-center relative'>
              <img
                src={closeIcon}
                width={14}
                height={14}
                onClick={() => {
                  toSetSiderOpen(false);
                }}
                className='absolute left-0'
              />
              <img src={logo99} width={30} height={30} style={{ borderRadius: 4 }} />
              <span className='font-DiDiSansPro-Medium text-xl text-[#333] ml-[14px] font-[500]'>
                99Abastece
              </span>
            </div>
          }
          style={{ background: '#E9EAEE' }}
          placement='left'
          maskClosable={false}
          open={isSiderOpen}
          width={'100%'}
          closeIcon={null}
          className={styles.drawer_sider}
        >
          <div className='flex flex-col' style={{ height: '100%' }}>
            <div className='mb-[21px] flex flex-col items-center font-DiDiSansPro-Regular text-base text-gray-8'>
              <Header width={56} height={56} className='mb-[8px]' />
              {loginInfo?.email}
            </div>
            <Menu
              style={{ flex: 1, overflow: 'scroll' }}
              selectedKeys={selectedKeys}
              defaultOpenKeys={menuList?.map((item: any) => item?.menu_name)}
              mode='inline'
              items={getItems(menuList)}
            />

            <div className='mb-[28px] flex justify-center font-DiDiSansPro-Regular text-base text-gray-8'>
              <div className='flex items-center flex-col mt-4'>
                <img src={customerServe} width={20} height={20} className='shrink-0' />
                <div className='mt-1'>{i18nTranslate('Internal_Tools_3_Cdzx_KDff')}</div>
                <div className='mb-[15px] px-2 text-center'>
                  {i18nTranslate('Internal_Tools_3_IpMC_owwp')}
                </div>
                <div
                  className='text-orange-500 underline'
                  onClick={() => {
                    setTimeout(() => {
                      goPassport(PASSPORT.logout);
                    }, 20);
                  }}
                >
                  {i18nTranslate('Internal_Tools_2_JYyb_uJky')}
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      ) : (
        <Sider
          className={styles.sider}
          style={{ background: '#E9EAEE' }}
          collapsedWidth={64}
          collapsible
          width={340}
          trigger={null}
          collapsed={collapsed}
        >
          <Menu
            style={{ height: 'calc(100vh - 144px)', overflow: 'scroll' }}
            selectedKeys={selectedKeys}
            defaultOpenKeys={!collapsed && menuList?.map((item: any) => item?.menu_name)}
            mode='inline'
            items={getItems(menuList)}
          />
          <div
            className='h-[64px] flex items-center justify-end border-t border-gray-6 px-[24px]'
            onClick={() => {
              setCollapsed(!collapsed);
            }}
          >
            {collapsed ? (
              <MenuExpand width={16} height={16} className='cursor-pointer' />
            ) : (
              <MenuFold width={16} height={16} className='cursor-pointer' />
            )}
          </div>
        </Sider>
      )}
    </>
  );
};

export default SiderComp;
